import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useFeature } from "@/lib/use-feature";
import { haQuantoTempo } from "@/lib/em-atendimento";

/**
 * A faixa que transforma a ficha em tela de atendimento: paciente, tempo decorrido, observação
 * da consulta e o botão de encerrar, sempre à vista enquanto o atendimento durar.
 *
 * A ficha continua sendo a ficha — a faixa só acrescenta o contexto do atendimento em curso;
 * sair dela (ou encerrar) devolve a tela ao estado normal.
 */
export function BarraDeAtendimento({ appointmentId }: { appointmentId: string }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const ditadoDisponivel = useFeature("atendimento.ditado");

  const query = trpc.appointment.getById.useQuery({ id: appointmentId });
  const consulta = query.data as any | undefined;

  const [obs, setObs] = useState("");
  const [obsCarregada, setObsCarregada] = useState(false);
  useEffect(() => {
    if (consulta && !obsCarregada) {
      setObs(consulta.notes ?? "");
      setObsCarregada(true);
    }
  }, [consulta, obsCarregada]);

  // Redesenha o tempo decorrido de minuto em minuto sem depender de refetch.
  const [, forcarRedesenho] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forcarRedesenho((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const salvarObs = trpc.appointment.update.useMutation({
    onSuccess: () => {
      utils.appointment.getById.invalidate({ id: appointmentId });
      toast.success("Observação salva.");
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar a observação"),
  });

  const encerrar = trpc.appointment.encerrarAtendimento.useMutation({
    onSuccess: () => {
      utils.appointment.invalidate();
      utils.team.list.invalidate();
      toast.success("Atendimento encerrado.");
      // Sai do modo atendimento sem tirar a pessoa da ficha.
      navigate(window.location.pathname, { replace: true });
    },
    onError: (e) => toast.error(e.message || "Erro ao encerrar o atendimento"),
  });

  if (query.isLoading) {
    return (
      <div className="mb-4 flex h-16 items-center justify-center rounded-2xl border border-cyan-200 bg-cyan-50/60 dark:border-cyan-500/30 dark:bg-cyan-500/5">
        <Loader2 className="h-4 w-4 animate-spin text-cyan-700 dark:text-cyan-300" />
      </div>
    );
  }
  if (!consulta) return null;

  const emCurso = consulta.status === "IN_PROGRESS";
  const tempo = haQuantoTempo(consulta.startedAt);

  return (
    <div className="mb-4 rounded-2xl border border-cyan-200 bg-cyan-50/60 p-4 dark:border-cyan-500/30 dark:bg-cyan-500/5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="relative flex h-2 w-2 shrink-0">
          {emCurso && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-500 opacity-60 motion-reduce:animate-none" />
          )}
          <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-600" />
        </span>
        <p className="min-w-0 flex-1 text-sm font-bold text-cyan-900 dark:text-cyan-200">
          {emCurso ? "Atendimento em andamento" : "Atendimento encerrado"}
          <span className="ml-2 font-medium text-cyan-800/70 dark:text-cyan-300/70">
            {consulta.procedure}
            {tempo && emCurso ? ` · começou ${tempo}` : ""}
          </span>
        </p>

        {ditadoDisponivel ? null : (
          <button
            type="button"
            disabled
            title="Em breve: ditar a observação em vez de digitar"
            className="inline-flex h-9 shrink-0 cursor-not-allowed items-center gap-1.5 rounded-md border border-cyan-200 px-2.5 text-xs font-semibold text-cyan-700/60 dark:border-cyan-500/30 dark:text-cyan-300/50"
          >
            <Mic className="h-3.5 w-3.5" />
            Observações ditadas
            <span className="rounded-full bg-cyan-100 px-1.5 py-0.5 text-[9px] font-bold uppercase dark:bg-cyan-500/20">
              Em breve
            </span>
          </button>
        )}

        {emCurso && (
          <button
            type="button"
            disabled={encerrar.isPending}
            onClick={() => encerrar.mutate({ id: appointmentId })}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
          >
            {encerrar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
            Encerrar atendimento
          </button>
        )}
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-cyan-800/70 dark:text-cyan-300/70">
          Observações da consulta
        </label>
        <textarea
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          onBlur={() => {
            if (obs !== (consulta.notes ?? "")) salvarObs.mutate({ id: appointmentId, data: { notes: obs } });
          }}
          rows={2}
          maxLength={2000}
          placeholder="O que aconteceu no atendimento..."
          className="w-full resize-y rounded-md border border-cyan-200 bg-white px-3 py-2 text-base text-zinc-900 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 sm:text-sm dark:border-cyan-500/30 dark:bg-card dark:text-foreground"
        />
      </div>
    </div>
  );
}
